import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  Flex,
  FlexProps,
  HStack,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  StackDivider,
  Text,
  VStack,
} from '@chakra-ui/react'
import { PopUpMessageContext, useApp, useCachedPractice } from '@hb/react'
import {
  AdminAssessmentData,
  Alert,
  Assessment,
  ASSESSMENT_SNIPPETS,
  ASSESSMENTS_ADMIN,
  AssessmentSnippet,
  CheckboxField,
  colors,
  defaultStageValidate,
  Field,
  FieldMapValue,
  FieldTypes,
  followStateMandatesCheckbox,
  getAlertText,
  getAssessmentName,
  getDateString,
  getNestedUserFields,
  getPracticeAssessmentCollectionPath,
  getPrimaryIsMedicaid,
  getSignedOnByPracticeDate,
  isField,
  maximumReimbursableChargesField,
  mergeAssessmentData,
  planDesignField,
  PopulatedAssessment,
  PopulatedUser,
  PracticeAssessmentData,
  PracticeAssessmentStage,
  reimbursementOptionField,
  StageHistoryItem,
  stateMandatesNotesField,
} from '@hb/shared'
import inNetwork, {
  checkboxDeductibleBasedOnCalendarYearField,
} from '@hb/shared/src/fields/callIn/inNetwork'
import { doc } from 'firebase/firestore'
import { set as nestedSet } from 'nested-property'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { ASSESSMENTS_REF } from '../../backend/db/collections'
import { useDocument } from '../../backend/db/hooks/useDocument'
import { usePopulatedAssessment } from '../../hooks/backend/user/usePopulatedAssessment'
import { useUpdateDoc } from '../../hooks/backend/useUpdateDoc'
import { AssessmentIcon } from '../../icons'
import { useUpdateAssessmentAnswers } from '../../utils/assessments/updateAssessmentAnswers'
import { ActionLog } from '../ActionLog'
import { SolidActionButton } from '../Buttons'
import { CopyId } from '../CopyId'
import { getRowBackground } from '../DataView'
import { Editable, EditableRow } from '../forms/Input'
import { PreviewSpan } from '../PreviewSpan'
import { StatusButton as StatusBadge } from '../Status/StatusButton'
import { MiscarriedPopover } from '../Users/Profile/ProfileMiscarried'
import { UserBadge } from '../Users/UserBadge'
import { AssessmentResultsLogItem } from './AssessmentResultsLogItem'

const deductibleCountsCheckbox: CheckboxField = {
  ...(inNetwork.children.deductibleCountsTowardOOPM as CheckboxField),
  type: FieldTypes.CHECKBOX,
}

const noBenefitsCheckbox: CheckboxField = {
  placeholder: 'No out of network benefits',
  type: FieldTypes.CHECKBOX,
}

const AlertsView = ({
  alerts,
  assessment,
  assessmentId,
}: {
  alerts?: Record<number, string | Alert>
  assessment: PopulatedAssessment | null
  assessmentId: string
}) => {
  const alertEntries = useMemo(() => Object.entries(alerts ?? {}), [alerts])
  return (
    <Flex align="center">
      {alertEntries.length ? (
        <VStack my={2} border="1px solid #990000" borderRadius={3} spacing={0} align="flex-start">
          <Text
            pl={2}
            bg="red.600"
            color="white"
            width="100%"
            fontSize="sm"
            mb={1}
            fontWeight="bold">
            ALERTS
          </Text>
          <HStack
            spacing={1}
            divider={<StackDivider />}
            align="flex-start"
            flexFlow="row wrap"
            px={1}
            pb={1}>
            {alertEntries.map(([date, alert]) => (
              <Flex key={date} px={1} align="center">
                <Text mr={2} color="gray.700" fontSize="sm" fontWeight={600}>
                  {getDateString(parseInt(date, 10), 'short')}
                </Text>
                <Text>{getAlertText(alert)}</Text>
              </Flex>
            ))}
          </HStack>
        </VStack>
      ) : null}
      {assessment?.miscarried ? (
        <MiscarriedPopover placement="right" assessment={assessment} assessmentId={assessmentId} />
      ) : null}
    </Flex>
  )
}

const updatedCoveragePath = 'insuranceInfo.primaryCoverage'
const HoverSummary = ({
  assessment,
  assessmentId,
}: {
  assessment: Assessment
  assessmentId: string
}) => {
  const { appName } = useApp()
  const mergedData = useMemo(() => mergeAssessmentData(assessment), [assessment])
  const isMedicaid = useMemo(() => getPrimaryIsMedicaid(mergedData?.insuranceInfo), [mergedData])
  const coverageData = useMemo(() => mergedData?.insuranceInfo?.primaryCoverage, [mergedData])

  const coverageCorrections = useMemo(
    () =>
      isMedicaid
        ? assessment.corrections?.insuranceInfo?.medicaidCoverage
        : assessment.corrections?.insuranceInfo?.primaryCoverage,
    [assessment, isMedicaid],
  )

  const noBenefits = useMemo(
    // () => getCorrectedValue(assessment, `${coveragePath}.out-of-network.noBenefits`),
    () =>
      coverageCorrections?.['out-of-network']?.noBenefits ??
      coverageData?.['out-of-network']?.noBenefits,
    [coverageCorrections, coverageData],
  )

  const { processResponse } = useContext(PopUpMessageContext)
  const updateAnswers = useUpdateAssessmentAnswers()
  const handleUpdate = useCallback(
    (path: string, updated: FieldMapValue) => {
      const data = {}
      nestedSet(data, path, updated)
      return updateAnswers({ id: assessmentId, appName, data })
        .then(() => processResponse({ success: 'Answers updated!' }))
        .catch(err => processResponse({ error: err.message }))
    },
    [assessmentId, processResponse, appName, updateAnswers],
  )

  const [editingSide, setEditingSide] = useState<'right' | 'left' | null>(null)
  const onLeftOpen = useCallback(() => {
    setEditingSide('left')
  }, [])

  const onRightOpen = useCallback(() => {
    setEditingSide('right')
  }, [])

  const onEditClose = useCallback(() => {
    setEditingSide(null)
  }, [])

  const { leftWidth, rightWidth } = useMemo(() => {
    if (editingSide === 'left') {
      return { leftWidth: '600px', rightWidth: '0px' }
    }
    if (editingSide === 'right') {
      return { leftWidth: '0px', rightWidth: '600px' }
    }
    return { leftWidth: '300px', rightWidth: '300px' }
  }, [editingSide])
  return (
    <VStack overflow="hidden" borderRadius={6} maxW="600px" spacing={0} divider={<Divider />}>
      <HStack divider={<StackDivider />} align="flex-start" spacing={0}>
        <VStack
          display={editingSide === 'right' ? 'none' : 'flex'}
          w={leftWidth}
          fontWeight={400}
          spacing={0}
          align="flex-start">
          <HStack w="100%" borderBottom="1px solid #cdcdcd" bg={colors.green.hex}>
            <Text pl={2} color="white" fontWeight={600}>
              In Network
            </Text>
          </HStack>
          <EditableRow
            small
            index={0}
            adminView
            openCallback={onLeftOpen}
            closeCallback={onEditClose}
            onSubmit={v => handleUpdate(`${updatedCoveragePath}.in-network.deductible`, v)}
            field={inNetwork.children.deductible as Field}
            label="Deductible"
            value={coverageData?.['in-network']?.deductible}
            correction={coverageCorrections?.['in-network']?.deductible}
          />
          <EditableRow
            small
            index={1}
            adminView
            openCallback={onLeftOpen}
            closeCallback={onEditClose}
            onSubmit={v => handleUpdate(`${updatedCoveragePath}.in-network.coinsurance`, v)}
            field={inNetwork.children.coinsurance as Field}
            label="Coinsurance"
            value={coverageData?.['in-network']?.coinsurance}
            correction={coverageCorrections?.['in-network']?.coinsurance}
          />
          <EditableRow
            small
            index={2}
            adminView
            openCallback={onLeftOpen}
            closeCallback={onEditClose}
            onSubmit={v => handleUpdate(`${updatedCoveragePath}.in-network.outOfPocketMax`, v)}
            field={inNetwork.children.outOfPocketMax as Field}
            label="Out of pocket max"
            value={coverageData?.['in-network']?.outOfPocketMax}
            correction={coverageCorrections?.['in-network']?.outOfPocketMax}
          />
          <Editable
            index={3}
            openCallback={onLeftOpen}
            adminView
            closeCallback={onEditClose}
            field={deductibleCountsCheckbox}
            value={coverageData?.['in-network']?.deductibleCountsTowardOOPM}
            correction={coverageCorrections?.['in-network']?.deductibleCountsTowardOOPM}
            onSubmit={v =>
              handleUpdate(`${updatedCoveragePath}.in-network.deductibleCountsTowardOOPM`, v)
            }
          />
          <EditableRow
            index={4}
            label="Maximum reimbursable charges"
            adminView
            openCallback={onLeftOpen}
            closeCallback={onEditClose}
            field={maximumReimbursableChargesField.children.amount as Field}
            value={coverageData?.['out-of-network']?.maximumReimbursableCharges?.amount}
            correction={coverageCorrections?.['out-of-network']?.maximumReimbursableCharges?.amount}
            onSubmit={v =>
              handleUpdate(
                `${updatedCoveragePath}.out-of-network.maximumReimbursableCharges.amount`,
                v,
              )
            }
          />
        </VStack>
        <VStack
          display={editingSide === 'left' ? 'none' : 'flex'}
          w={rightWidth}
          fontWeight={400}
          spacing={0}
          align="flex-start">
          <HStack w="100%" borderBottom="1px solid #cdcdcd" bg={colors.green.hex}>
            <Text pl={2} color="white" fontWeight={600}>
              Out of Network
            </Text>
          </HStack>
          <Editable
            index={0}
            openCallback={onRightOpen}
            closeCallback={onEditClose}
            adminView
            field={noBenefitsCheckbox}
            value={coverageData?.['out-of-network']?.noBenefits}
            correction={coverageCorrections?.['out-of-network']?.noBenefits}
            style={{ width: '100%' }}
            onSubmit={v => handleUpdate(`${updatedCoveragePath}.out-of-network.noBenefits`, v)}
          />
          {noBenefits ? null : (
            <>
              <EditableRow
                small
                openCallback={onRightOpen}
                closeCallback={onEditClose}
                adminView
                index={1}
                onSubmit={v => handleUpdate(`${updatedCoveragePath}.out-of-network.deductible`, v)}
                field={inNetwork.children.deductible as Field}
                label="Deductible"
                value={coverageData?.['out-of-network']?.deductible}
                correction={coverageCorrections?.['out-of-network']?.deductible}
              />
              <EditableRow
                small
                openCallback={onRightOpen}
                closeCallback={onEditClose}
                adminView
                index={2}
                onSubmit={v => handleUpdate(`${updatedCoveragePath}.out-of-network.coinsurance`, v)}
                field={inNetwork.children.coinsurance as Field}
                label="Coinsurance"
                value={coverageData?.['out-of-network']?.coinsurance}
                correction={coverageCorrections?.['out-of-network']?.coinsurance}
              />
              <EditableRow
                small
                index={3}
                openCallback={onRightOpen}
                closeCallback={onEditClose}
                adminView
                onSubmit={v =>
                  handleUpdate(`${updatedCoveragePath}.out-of-network.outOfPocketMax`, v)
                }
                field={inNetwork.children.outOfPocketMax as Field}
                label="Out of pocket max"
                value={coverageData?.['out-of-network']?.outOfPocketMax}
                correction={coverageCorrections?.['out-of-network']?.outOfPocketMax}
              />
              <Editable
                index={4}
                openCallback={onRightOpen}
                closeCallback={onEditClose}
                adminView
                field={deductibleCountsCheckbox}
                value={coverageData?.['out-of-network']?.deductibleCountsTowardOOPM}
                onSubmit={v =>
                  handleUpdate(
                    `${updatedCoveragePath}.out-of-network.deductibleCountsTowardOOPM`,
                    v,
                  )
                }
              />
            </>
          )}

          <Editable
            index={5}
            openCallback={onRightOpen}
            adminView
            closeCallback={onEditClose}
            field={checkboxDeductibleBasedOnCalendarYearField}
            value={coverageData?.['in-network']?.deductibleBasedOnCalendarYear}
            onSubmit={v =>
              handleUpdate(`${updatedCoveragePath}.in-network.deductibleBasedOnCalendarYear`, v)
            }
          />
        </VStack>
      </HStack>
      <VStack w="100%" fontWeight={400} spacing={0} align="flex-start">
        <HStack w="100%" borderBottom="1px solid #cdcdcd" bg={colors.green.hex}>
          <Text color="white" pl={2} fontWeight={600}>
            Plan Design
          </Text>
        </HStack>
        <EditableRow
          small
          index={6}
          adminView
          label="Plan Design"
          field={planDesignField}
          value={coverageData?.['plan-design-and-state-mandates']?.planDesign}
          onSubmit={v =>
            handleUpdate(`${updatedCoveragePath}.plan-design-and-state-mandates.planDesign`, v)
          }
        />
        <Editable
          size="sm"
          index={7}
          adminView
          field={followStateMandatesCheckbox}
          value={coverageData?.['plan-design-and-state-mandates']?.followsStateMandates}
          onSubmit={v =>
            handleUpdate(
              `${updatedCoveragePath}.plan-design-and-state-mandates.followsStateMandates`,
              v,
            )
          }
        />
        <EditableRow
          label="State mandates notes"
          small
          adminView
          index={8}
          field={stateMandatesNotesField}
          value={coverageData?.['plan-design-and-state-mandates']?.followsStateMandatesNote}
          onSubmit={v =>
            handleUpdate(
              `${updatedCoveragePath}.plan-design-and-state-mandates.followsStateMandatesNote`,
              v,
            )
          }
        />
        <EditableRow
          label="Reimbursement option"
          small
          index={9}
          adminView
          field={reimbursementOptionField}
          value={coverageData?.['out-of-network']?.reimbursementOption?.optionText}
          onSubmit={v =>
            handleUpdate(`${updatedCoveragePath}.out-of-network.reimbursementOption.optionText`, v)
          }
        />
      </VStack>
    </VStack>
  )
}

const SignedOnByPracticeView = ({
  signedOnByPracticeDate,
}: {
  signedOnByPracticeDate: StageHistoryItem<PracticeAssessmentStage> | 'unknown' | null
}) => {
  if (signedOnByPracticeDate === 'unknown') {
    return (
      <Flex gap={1.5} align="center">
        <Badge colorScheme="red" fontSize="sm" fontWeight="bold">
          ?
        </Badge>
        <Text fontFamily="Open Sans" color="gray.600" fontSize="0.9rem">
          Patient signed on by practice (unknown date)
        </Text>
      </Flex>
    )
  }
  if (signedOnByPracticeDate) {
    return (
      <ActionLog
        action="Patient signed on by practice"
        group="practice"
        by={signedOnByPracticeDate.by}
        on={signedOnByPracticeDate.on}
        notes={signedOnByPracticeDate?.notes}
      />
    )
  }
  return null
}

interface CheckboxItemData {
  label: string
  checked: boolean
  userId?: string | null
  userGroup?: 'patient' | 'practice' | 'admin'
  date?: number | null
  index?: number
}

const PracticeCheckboxItem = ({
  label,
  checked,
  userId,
  userGroup,
  date,
  index,
}: CheckboxItemData) => (
  <HStack bg={getRowBackground(index)} py={1} px={2} spacing={1.5} w="100%" align="center">
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      w="18px"
      h="18px"
      border="2px solid"
      borderColor={checked ? 'green.500' : 'gray.300'}
      borderRadius="3px"
      bg={checked ? 'green.500' : 'white'}
      color="white"
      fontSize="12px"
      flexShrink={0}>
      {checked ? '✓' : ''}
    </Box>
    <Text fontFamily="Open Sans" color="gray.600" flex={1}>
      {label}
    </Text>
    {date ? (
      <Text fontFamily="Open Sans" color="gray.500" fontSize="0.85rem" pr={1} fontWeight={500}>
        {getDateString(date, 'short')}
      </Text>
    ) : null}
    {checked && userId ? <UserBadge userGroup={userGroup} userId={userId} size="xs" /> : null}
  </HStack>
)

const PracticeAssessmentActions = ({
  assessment,
  user,
  onlyIncomplete,
}: {
  assessment: PopulatedAssessment | null
  user: PopulatedUser
  onlyIncomplete?: boolean
}) => {
  const {
    summarySubmittedOn,
    summarySubmittedBy,
    sentOn,
    submittedOn,
    submittedBy,
    scheduledCall,
    resultsViewedOn,
    signedOnDate,
    sentBy,
  } = assessment ?? {}

  const { consentFormsStatus } = user ?? {}

  // Calculate consent forms count
  const { totalConsentForms, signedConsentForms } = useMemo(() => {
    if (!consentFormsStatus) return { totalConsentForms: 0, signedConsentForms: 0 }
    const forms = Object.values(consentFormsStatus)
    return {
      totalConsentForms: forms.length,
      signedConsentForms: forms.filter(form => !!form.signedOn).length,
    }
  }, [consentFormsStatus])

  const checkboxItems: CheckboxItemData[] = useMemo(() => {
    const allFormsSigned = totalConsentForms > 0 && signedConsentForms === totalConsentForms
    const allItems: CheckboxItemData[] = [
      // {
      //   label: 'Questionnaire Submitted',
      //   checked: !!submittedOn,
      //   userId: submittedBy,
      //   userGroup: 'patient',
      //   date: submittedOn,
      // },
      {
        label: 'Patient Forms Completed',
        checked: !!(summarySubmittedOn || submittedOn),
        userId: summarySubmittedBy || submittedBy,
        userGroup: 'patient',
        date: summarySubmittedOn,
      },
      {
        label: 'Assessment Notification Sent',
        checked: !!sentOn,
        userId: sentBy,
        userGroup: 'admin',
        date: sentOn,
      },
      {
        label: 'Call scheduled by patient',
        checked: !!(scheduledCall?.scheduledOn ?? scheduledCall?.callDate),
        userId: assessment?.patientId,
        userGroup: 'patient',
        date: scheduledCall?.scheduledOn ?? scheduledCall?.callDate,
      },
      {
        label: 'Call occurred with HB',
        checked: !!scheduledCall?.reportedOn,
        userId: scheduledCall?.reportedBy,
        userGroup: 'admin',
        date: scheduledCall?.reportedOn,
      },
      {
        label: 'Assessment Viewed',
        checked: !!resultsViewedOn,
        userId: assessment?.patientId,
        userGroup: 'patient',
        date: resultsViewedOn,
      },
      {
        label: 'Sign On Form Complete',
        checked: !!signedOnDate,
        userId: assessment?.patientId,
        userGroup: 'patient',
        date: signedOnDate,
      },
      {
        label: `${allFormsSigned ? 'All' : `${signedConsentForms}/${totalConsentForms}`} Consent Forms Signed`,
        checked: totalConsentForms > 0 && signedConsentForms === totalConsentForms,
        userId: signedConsentForms > 0 ? assessment?.patientId : undefined,
        userGroup: 'patient',
      },
    ]
    return onlyIncomplete ? allItems.filter(item => !item.checked) : allItems
  }, [
    onlyIncomplete,
    summarySubmittedOn,
    summarySubmittedBy,
    submittedOn,
    submittedBy,
    sentOn,
    sentBy,
    scheduledCall,
    resultsViewedOn,
    signedOnDate,
    assessment?.patientId,
    signedConsentForms,
    totalConsentForms,
  ])

  return (
    <VStack py={1} spacing={1} align="flex-start" w="100%">
      {checkboxItems.map((item, index) => (
        <PracticeCheckboxItem key={index} index={index} {...item} />
      ))}
    </VStack>
  )
}

const AssessmentActions = ({
  assessment,
  adminData,
}: {
  assessment: PopulatedAssessment | null
  adminData: AdminAssessmentData | null
}) => {
  const {
    submittedOn,
    sentOn,
    submittedBy,
    answersUpdatedOn,
    answersUpdatedBy,
    correctionsUpdatedBy,
    correctionsUpdatedOn,
    answersUpdatedByGroup,
    submittedByGroup,
    scheduledCall,
    patientId,
    resultsViewedOn,
    previousResults,
    resultsInsurerId,
    sentBy,
    signedOnDate,
    practiceStages,
    practiceStageHistory,
  } = assessment ?? {}
  const signedOnByPracticeDate = useMemo(
    () => getSignedOnByPracticeDate(practiceStages ?? [], practiceStageHistory),
    [practiceStages, practiceStageHistory],
  )
  const { draftSavedBy, draftSavedOn } = adminData ?? {}
  return (
    <VStack spacing={1} align="flex-start">
      {signedOnByPracticeDate ? (
        <SignedOnByPracticeView signedOnByPracticeDate={signedOnByPracticeDate} />
      ) : null}
      {signedOnDate ? (
        <ActionLog
          action="Sign-on form completed"
          group="patient"
          by={patientId}
          on={signedOnDate}
        />
      ) : null}
      {resultsViewedOn ? (
        <ActionLog action="Assessment viewed" group="patient" by={patientId} on={resultsViewedOn} />
      ) : null}
      {scheduledCall?.callDate ? (
        <ActionLog
          action="HB call occurred"
          group="admin"
          by={scheduledCall.reportedBy}
          on={scheduledCall.reportedOn}
        />
      ) : null}
      {scheduledCall?.scheduledOn ? (
        <ActionLog
          action="HB call scheduled"
          group="patient"
          by={patientId}
          on={scheduledCall.scheduledOn}
        />
      ) : null}
      {sentOn ? (
        <AssessmentResultsLogItem
          insurerId={resultsInsurerId}
          sentOn={sentOn}
          group="admin"
          sentBy={sentBy}
        />
      ) : null}
      {draftSavedBy || draftSavedOn ? (
        <ActionLog action="Draft saved" group="admin" on={draftSavedOn} by={draftSavedBy} />
      ) : null}
      {previousResults?.length
        ? previousResults.map(res => (
            <AssessmentResultsLogItem
              key={`${res.sentOn}`}
              insurerId={res.insuranceProviderId}
              group="admin"
              sentOn={res.sentOn}
              sentBy={res.sentBy}
            />
          ))
        : null}
      {correctionsUpdatedBy || correctionsUpdatedOn ? (
        <ActionLog
          action="Corrections updated"
          // group={correctionsUpdatedByGroup ?? 'admin'}
          group="admin"
          on={correctionsUpdatedOn}
          by={correctionsUpdatedBy}
        />
      ) : null}
      {answersUpdatedBy || answersUpdatedOn ? (
        <ActionLog
          group={answersUpdatedByGroup}
          action="Answers updated"
          on={answersUpdatedOn}
          by={answersUpdatedBy}
        />
      ) : null}
      {submittedBy || submittedOn ? (
        <ActionLog
          action="Answers submitted"
          group={submittedByGroup ?? (submittedBy === patientId ? 'patient' : 'admin')}
          on={submittedOn}
          by={submittedBy}
        />
      ) : null}
    </VStack>
  )
}

const AssessmentMissingFields = ({
  assessment,
  mergedData,
}: {
  assessment: PopulatedAssessment | null
  mergedData: FieldMapValue
}) => {
  const nestedUserFields = useMemo(
    () => getNestedUserFields(true, assessment ?? null),
    [assessment],
  )
  const missingFields = useMemo(() => {
    const missing = defaultStageValidate(nestedUserFields, true)(mergedData)
    if (missing) {
      const missingNames = Object.keys(missing).map(key => {
        const child = nestedUserFields.children[key]
        return isField(child) ? child.placeholder : child.name
      })
      return missingNames
    }
    return []
  }, [mergedData, nestedUserFields])
  return (
    <Box py={1} px={2} my={1} border="1px solid" borderColor={colors.red.hex} borderRadius={2}>
      <PreviewSpan
        style={{
          width: '100%',
          fontWeight: 600,
          color: colors.red.hex,
          lineHeight: 1,
        }}>
        Questionnaire Incomplete:
      </PreviewSpan>
      {missingFields ? (
        <Box>
          {missingFields.map(fieldName => (
            <Text key={fieldName} style={{ color: colors.red.hex }}>
              {fieldName}
            </Text>
          ))}
        </Box>
      ) : null}
    </Box>
  )
}

export const AssessmentPreview: React.FC<{
  user: PopulatedUser
  id: string
  assessment: Assessment | null
  admin?: boolean
  selected?: boolean
  preview?: boolean
  onSelect: () => void
  buttonText?: string
}> = ({ assessment, onSelect, id, buttonText = 'Open', selected, preview, user }) => {
  const assessmentRef = useMemo(() => doc(ASSESSMENTS_REF, id), [id])

  const { appName } = useApp()
  const { data: practice } = useCachedPractice(
    appName !== 'providers-app' ? (assessment?.midwifeId ?? null) : null,
  )
  const update = useUpdateDoc('pregnancy')
  const { data: adminData } = useDocument<AdminAssessmentData>(
    ASSESSMENTS_ADMIN,
    appName === 'providers-app' ? null : id,
  )
  const { data: assessmentSnippet } = useDocument<AssessmentSnippet>(
    appName === 'providers-app' ? null : ASSESSMENT_SNIPPETS,
    id,
  )
  const { data: practiceData } = useDocument<PracticeAssessmentData>(
    assessment?.midwifeId ? getPracticeAssessmentCollectionPath(assessment.midwifeId) : null,
    id,
  )
  const populatedAssessment = usePopulatedAssessment(
    assessment,
    user,
    assessmentSnippet,
    adminData,
    practiceData,
  )

  // const calculatedStatus = useMemo(
  //   () => (populatedAssessment ? getAssessmentStatus(true, populatedAssessment) : null),
  //   [populatedAssessment],
  // )

  const { archivedOn } = assessment ?? {}
  const { alerts } = adminData ?? {}
  // const { miscarried } = populatedAssessment ?? {}

  const hasAlerts = useMemo(() => alerts && !!Object.keys(alerts).length, [alerts])

  const mergedData = useMemo(() => mergeAssessmentData(assessment ?? undefined), [assessment])

  const edd = useMemo(() => mergedData?.delivery?.edd, [mergedData])

  const { status, deliveredOn } = populatedAssessment ?? {}

  const deliveredOnText = useMemo(() => {
    if (!deliveredOn) return 'No'
    if (deliveredOn === Infinity) return 'Transfer'
    return getDateString(deliveredOn, 'short')
  }, [deliveredOn])
  return (
    <Flex
      flexFlow="column"
      align="flex-start"
      position="relative"
      w="100%"
      borderRadius={3}
      px={preview ? 2 : 3}
      py={preview ? 1 : 2}
      border="1px solid"
      bg="white"
      borderColor={selected ? '#cdcdcd' : 'transparent'}
      boxShadow={selected ? 'inset 0 0 6px rgb(255, 180, 180)' : '1px 1px 4px #00000066'}>
      <HStack px={2} w="100%" mb={0} pt={2} fontWeight={600} justify="space-between">
        <Editable
          onSubmit={v => update(assessmentRef, 'name', v)}
          value={assessment ? getAssessmentName(assessment) : 'Loading...'}
          field={{ type: FieldTypes.TEXT, placeholder: 'Assessment Name' }}
          style={{ flex: 1, minWidth: 0 }}
          dataCellProps={{
            fontSize: '16px',
            fontFamily: 'hero-new',
            minWidth: 0,
            textOverflow: 'ellipsis',
            maxW: '100%',
            display: 'block',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            flex: 1,
            fontWeight: 500,
          }}
        />
        {archivedOn ? (
          <Badge colorScheme="red" fontSize="xs" fontWeight="bold">
            ARCHIVED {getDateString(archivedOn, 'short')}{' '}
          </Badge>
        ) : null}
        {appName === 'app' ? (
          <Popover
            strategy="fixed"
            placement="bottom-end"
            isLazy
            closeOnBlur={false}
            trigger="hover">
            <PopoverTrigger>
              <SolidActionButton
                size={preview ? 'xs' : 'sm'}
                variant="solid"
                onClick={onSelect}
                ml="auto">
                {buttonText}
              </SolidActionButton>
            </PopoverTrigger>
            <PopoverContent boxShadow="1px 1px 6px #00000077" width="auto">
              <PopoverArrow />
              {assessment ? (
                <HoverSummary assessmentId={id} assessment={assessment} />
              ) : (
                <HStack>
                  <CircularProgress isIndeterminate size={5} />
                  <Text>Loading...</Text>
                </HStack>
              )}
            </PopoverContent>
          </Popover>
        ) : (
          <SolidActionButton size="sm" variant="solid" onClick={onSelect} ml="auto">
            {buttonText}
          </SolidActionButton>
        )}
      </HStack>
      {hasAlerts || populatedAssessment?.miscarried ? (
        <AlertsView assessment={populatedAssessment} assessmentId={id} />
      ) : null}
      <Box pb={2} w="100%">
        <Box bottom="3px" position="relative">
          <StatusBadge
            position="relative"
            fontSize="sm"
            type="assessment"
            status={status || 'awaiting-questionnaire'}
          />
        </Box>
        <Box px={2} pb={2}>
          <HStack align="center">
            <Text fontWeight="bold" color="gray.500" fontSize="sm">
              EDD
            </Text>
            <Text lineHeight={1}>{edd ? getDateString(edd, 'short') : 'None'}</Text>
          </HStack>
          <HStack align="center">
            <Text fontWeight="bold" color="gray.500" fontSize="sm">
              DELIVERED
            </Text>
            <Text lineHeight={1}>{deliveredOnText}</Text>
          </HStack>
          {!preview && appName !== 'providers-app' ? (
            <HStack spacing={1}>
              <Text fontWeight="bold" color="gray.500" fontSize="sm">
                PRACTICE
              </Text>
              <Text opacity={practice ? 1 : 0.7} fontSize="sm">
                {practice ? (practice.name ?? 'Unnamed practice') : 'None'}
              </Text>
            </HStack>
          ) : null}
          {preview ? null : <Divider my={2} />}
          {preview ? null : (
            <>
              {status === 'incomplete' ? (
                <AssessmentMissingFields assessment={populatedAssessment} mergedData={mergedData} />
              ) : null}
            </>
          )}
          {appName === 'providers-app' ? (
            <PracticeAssessmentActions
              onlyIncomplete={preview}
              assessment={populatedAssessment}
              user={user}
            />
          ) : (
            <AssessmentActions assessment={populatedAssessment} adminData={adminData} />
          )}
        </Box>
        {/* {appName === 'app' && !preview ? (
          <Box w="100%">
            <CollapsibleInsurancePlansView />
          </Box>
        ) : null} */}
        {preview ? null : <CopyId buttonProps={{ right: 2, bottom: 2 }} item={{ id }} />}
      </Box>
    </Flex>
  )
}

export const BasicPregnancyPreview = ({
  pregnancy,
  onSelect,
  flexProps,
  shortName,
  isThisPregnancy,
}: {
  pregnancy: Assessment
  onSelect?: () => void
  flexProps?: FlexProps
  shortName?: boolean
  isThisPregnancy?: boolean
}) => {
  const { archivedOn } = pregnancy
  const edd = useMemo(
    () => pregnancy?.corrections?.delivery?.edd ?? pregnancy?.data?.delivery?.edd,
    [pregnancy],
  )

  return (
    <Flex
      flexFlow="column"
      align="flex-start"
      position="relative"
      w="100%"
      borderRadius={3}
      bg="white"
      boxShadow="1px 1px 4px #00000066"
      {...flexProps}>
      <HStack align="flex-start" px={2} pt={1.5} w="100%" mb={0} justify="space-between">
        <Flex flex={1} minW={0} align="center">
          <AssessmentIcon />
          <Text
            isTruncated
            fontSize="sm"
            fontFamily="Hero-New"
            fontWeight={500}
            position="relative">
            {getAssessmentName(pregnancy, undefined, shortName)}
            {isThisPregnancy ? ' (This Pregnancy)' : ''}
          </Text>
        </Flex>
        {onSelect ? (
          <SolidActionButton size="xs" onClick={onSelect} ml="auto">
            View
          </SolidActionButton>
        ) : null}
      </HStack>
      <Box px={3} pb={2} w="100%">
        <HStack>
          <Text fontWeight="bold" color="gray.500" fontSize="sm">
            EDD
          </Text>
          <Text lineHeight={1}>{edd ? getDateString(edd, 'short') : 'None'}</Text>
        </HStack>
        {archivedOn ? (
          <Badge colorScheme="red" fontSize="xs" fontWeight="bold">
            ARCHIVED {getDateString(archivedOn, 'short')}{' '}
          </Badge>
        ) : null}
      </Box>
    </Flex>
  )
}
